import {
  CreateProductInput,
  MarkupType,
  Product,
  Uom,
  UpdateProductInput,
} from "@towersystems/roam-common/lib/generated-types";
import { get } from "lodash";
import React from "react";
import { isValidMoneyValue } from "../../../utilities/is-valid-money";
import { ProductFormValues } from "../components/ProductForm/types";

const floatRegex = /^[-+]?(\d*\.\d+|\d+\.\d*|\d+)$/;
const positiveFloatRegex = /^[0-9]*\.?[0-9]+$/;

export function testFloatAndMoney(value: string) {
  if (!value) {
    return false; // If identifier is empty, return false
  }
  // Use parseFloat to cast the string to a number
  const numberValue = parseFloat(value);
  if (isNaN(numberValue)) {
    return false;
  }
  return isValidMoneyValue(value);
}

export function testPositiveFloat(value: string) {
  if (!value) {
    return false; // If identifier is empty, return false
  }
  // Use parseFloat to cast the string to a number
  const numberValue = parseFloat(value);

  if (isNaN(numberValue)) {
    return false;
  }

  return positiveFloatRegex.test(value);
}

export function testFloat(value: string) {
  if (!value) {
    return false; // If identifier is empty, return false
  }
  // Use parseFloat to cast the string to a number
  const numberValue = parseFloat(value);
  if (isNaN(numberValue)) {
    return false;
  }
  return floatRegex.test(value);
}

export const productFormDataToGraphqlInput = (input: ProductFormValues) => {
  let output: UpdateProductInput | CreateProductInput = {
    name: input.name,
    stockNumber: input.stockNumber,
    departmentId: input.departmentId,
    categoryId: input.categoryId?.length ? input.categoryId : undefined,

    //@ts-expect-error
    supplierId: input.supplierId?.length ? input.supplierId : null,

    supplierStockCode: input.supplierStockCode?.length
      ? input.supplierStockCode
      : "",
    serialNumbersArr: input.serialNumbersArr ? input.serialNumbersArr : false,
    serialNumbersSale: input.serialNumbersSale
      ? input.serialNumbersSale
      : false,
    locationInventoryPricing: [
      {
        locationId: input.locationId,
        taxGroupId: input.taxGroupId?.length ? input.taxGroupId : undefined,
        costPrice: parseFloat(input.costPrice),
        retailPrice: parseFloat(input.retailPrice),
        tradePrice: parseFloat(input.tradePrice),
      },
    ],
    tags: input.tags?.length ? input.tags : [],
    priceLookup: input.priceLookup?.length ? input.priceLookup : "",
    description: input.description?.length ? input.description : "",
    weight: input.weight?.length ? parseFloat(input.weight) : undefined,
    UOM: (input.UOM ? input.UOM : Uom.KG) as Uom,
    selectedOptions: input.selectedOptions ?? [],
  };

  if (input.id) {
    output = { ...output, id: input.id };
  }

  return output;
};

/**
 * MUST provide default values for all fields, including those that are only for ui purposes.
 * Numbers must also be formatted correctly, toFixed(2), otherwise the form will be become dirty
 * as react-hook-form just compares default object values to the current values.
 */
export const productEntityToFormData = (
  product: Product,
  locationId: string
): Partial<ProductFormValues> => {
  let locationInventoryPricing = product.locationInventoryPricing.find(
    (lip) => lip.locationId === locationId
  );

  locationInventoryPricing = !locationInventoryPricing
    ? product.locationInventoryPricing[0]
    : locationInventoryPricing;

  let retailMarkup =
    product.category?.markup?.find((c) => c.markupType == MarkupType.RETAIL) ??
    product.department?.markup?.find((c) => c.markupType == MarkupType.RETAIL);

  let tradeMarkup =
    product.category?.markup?.find((c) => c.markupType == MarkupType.TRADE) ??
    product.department?.markup?.find((c) => c.markupType == MarkupType.TRADE);

  const isMainProduct = product.variantParentId === product.id;

  const parentOptions =
    (isMainProduct ? product.options : product.variantParent?.options) ?? [];

  const normalizedOptions = parentOptions.map((o) => {
    const maybeMatching = product.selectedOptions.find((so) => {
      return so.name === o.name;
    });
    return (
      maybeMatching ?? {
        name: o.name,
        value: "",
      }
    );
  });

  return {
    id: product.id,
    locationId,
    departmentId: product.departmentId ?? undefined,
    categoryId: product.categoryId ?? undefined,
    name: product.name ?? "",
    stockNumber: product.stockNumber ?? "",
    supplierId: product.supplierId ?? undefined,
    supplierStockCode: product.supplierStockCode ?? "",
    taxGroupId: product.locationInventoryPricing[0]?.taxGroupId ?? undefined,
    costPrice: locationInventoryPricing?.costPrice.toFixed(2) ?? "0",
    retailPrice:
      product.productKit?.price?.toFixed(2) ??
      locationInventoryPricing?.retailPrice.toFixed(2) ??
      "0",
    tradePrice: locationInventoryPricing?.tradePrice.toFixed(2) ?? "0",
    costPriceExlTax:
      locationInventoryPricing?.costPriceExclTax.toFixed(2) ?? "0",
    markupPercent: retailMarkup?.markupPercentage ?? 0,
    tradeMarkupPercent: tradeMarkup?.markupPercentage ?? 0,
    tradeMarkupType: tradeMarkup?.markupStyle ?? "",
    taxRatePercent: locationInventoryPricing?.taxRatePercent ?? 0,
    serialNumbersArr: product.serialNumbersArr ?? false,
    serialNumbersSale: product.serialNumbersSale ?? false,
    tags: product.tags ?? [],
    priceLookup: product.priceLookup ?? "",
    description: product.description ?? "",
    weight: product.weight?.toFixed(2) ?? "0",
    UOM: product.UOM ?? Uom.KG,
    selectedOptions: normalizedOptions,
    globalDisable: !!product.productKit,
  };
};

// useful for resetting the form when a server error occurs
export const useResetFormOnRootError = ({
  context,
  field = "root",
}: {
  context: any;
  field?: string;
}) => {
  const { formState, trigger } = context;
  const { errors } = formState;
  React.useEffect(() => {
    if (get(errors, field)) {
      trigger();
    }
  }, [get(errors, field)]);
};

export function timeout(delay: number) {
  return new Promise((res) => setTimeout(res, delay));
}
